






import Vue, { PropType } from 'vue'

import { ValidationInfo } from './Validation'

interface InputValidationInfo {
  valid: boolean
  errors: string[]
}

interface InputBucket {
  input: any
  unwatchValid: () => void
  unwatchErrors: () => void
  errorBucketInitialized: boolean
  validationInfo: InputValidationInfo | null
}

export default Vue.extend({
  props: {
    value: {
      type: Object as PropType<ValidationInfo>,
      required: false,
      validator (info: ValidationInfo) {
        return true
      }
    }
  },

  data: () => ({
    inputs: [] as InputBucket[]
  }),

  provide () {
    return {
      /** Provide as a form for children */
      form: this
    }
  },

  /** Inject the parent form so we can register with it */
  inject: ['form'],

  created () {
    let self = this as any
    // Register with the form provider as a validatable on behalf of all validatable children
    self.form.register(this)
  },

  beforeDestroy () {
    let self = this as any
    self.form.unregister(this)
  },

  methods: {
    /** Lets child validatables register */
    register (input: any): void {
      this.inputs.push({
        input: input,
        unwatchValid: this.$watch(
          () => input.valid,
          () => {
            // When the input's valid-status changes, update info
            let self = this as any
            self.updateInputValidationInfo(input)
            self.updateValue()
          }
        ),
        unwatchErrors: this.$watch(
          () => input.errorBucket,
          errorBucket => {
            // The watch on .valid doesn't always fire on the first change
            // A change in the errorBucket array can also indicate a change in valid status,
            // but the initial change must be ignored
            let self = this as any
            let inputBucket = this.inputs.find(x => x.input == input)
            if (!inputBucket!.errorBucketInitialized) {
              inputBucket!.errorBucketInitialized = true
              return
            }
            self.updateInputValidationInfo(input)
            self.updateValue()
          }
        ),
        errorBucketInitialized: false,
        validationInfo: null
      })
    },

    /** Lets child validatables unregister */
    unregister (input: any) {
      let index = this.inputs.findIndex(x => x.input == input)
      if (index >= 0) {
        this.inputs[index].unwatchValid()
        this.inputs[index].unwatchErrors()
        this.inputs.splice(index, 1)
      }
    },

    /** Called by the form (or the parent group) to validate the group */
    validate (force: boolean = false): boolean {
      let self = this as any

      for (const bucket of this.inputs) {
        bucket.input.validate(force)
        self.updateInputValidationInfo(bucket.input)
      }
      let info = self.getValidationInfo()
      self.updateValue(info)

      return info.valid
    },

    /**
     * Updates the validation info for the specified child validatable
     * @updateMessagesOnly If true, only update if the input is already registered as invalid. Avoids triggering validation in the input.
     */
    updateInputValidationInfo (input: any): void {
      let self = this as any
      let bucket = this.inputs.find(x => x.input == input)
      if (!bucket) return

      bucket.validationInfo = {
        valid: input.valid,
        errors: input.valid ? [] : self.formatErrors(input)
      }
    },

    /** Gets a summary of all validated inputs */
    getValidationInfo (): ValidationInfo {
      let self = this as any
      let info = {
        valid: this.inputs.filter(x => x.validationInfo).every(x => x.validationInfo!.valid),
        errors: this.inputs.filter(x => x.validationInfo).flatMap(x => x.validationInfo!.errors)
      }
      return info
    },

    /** Returns a list of error messages for the input, each prefixed with the input's label */
    formatErrors (input: any): string[] {
      if (input.valid || !input.errorBucket) return []
      let prefix = input.label ? input.label + ': ' : ''
      return (input.errorBucket as string[]).map(x => prefix + x)
    },

    /** Emits an event to update the v-model value */
    updateValue (info: ValidationInfo | null): void {
      let self = this as any
      this.$emit('input', info ? info : self.getValidationInfo())
    }
  }
})
